import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { API } from "../App";
import { formatApiError } from "../lib/utils";

const empty = { code: "", duration_months: 24, plan_id: "plan_pro", description: "" };

export const PromoCodesAdmin = () => {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setPromos(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load promo codes"));
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.code.trim().length < 3) return setError("Code must be at least 3 characters");
    try {
      await axios.post(`${API}/admin/promo-codes`, { ...form, duration_months: Number(form.duration_months) }, { withCredentials: true });
      toast.success("Promo code created");
      setForm(empty);
      setError("");
      load();
    } catch (err) {
      setError(formatApiError(err, "Could not create code"));
    }
  };

  const update = async (id, data) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${id}`, data, { withCredentials: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this promo code? Users who already redeemed it keep their benefit.")) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { withCredentials: true });
      toast.success("Promo code deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-promo-section">
      <form onSubmit={create} className="bg-zinc-900 border border-zinc-800 p-6 grid gap-4 md:grid-cols-[1fr_140px_1fr_auto] items-end">
        <div>
          <Label className="text-zinc-300">Code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SUMMER2026"
            className="mt-1 bg-zinc-800 border-zinc-700 font-mono" data-testid="promo-create-code-input" />
        </div>
        <div>
          <Label className="text-zinc-300">Free months</Label>
          <Input type="number" min={1} max={120} value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
            className="mt-1 bg-zinc-800 border-zinc-700" data-testid="promo-create-months-input" />
        </div>
        <div>
          <Label className="text-zinc-300">Note (optional)</Label>
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Who is this for?"
            className="mt-1 bg-zinc-800 border-zinc-700" data-testid="promo-create-note-input" />
        </div>
        <Button type="submit" className="bg-orange-600 hover:bg-orange-500" data-testid="promo-create-btn">Create code</Button>
        {error && <p className="text-red-400 text-sm md:col-span-4" data-testid="promo-create-error">{error}</p>}
      </form>

      <div className="bg-zinc-900 border border-zinc-800 divide-y divide-zinc-800" data-testid="promo-list">
        {promos.length === 0 && <p className="text-zinc-500 text-center py-8">No promo codes yet.</p>}
        {promos.map((p) => (
          <div key={p.promo_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between" data-testid={`promo-row-${p.code}`}>
            <div>
              <p className="font-mono text-orange-500 text-lg">{p.code}</p>
              <p className="text-zinc-400 text-sm">Pro free for {p.duration_months} months{p.description ? ` · ${p.description}` : ""}</p>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-zinc-300 text-sm" data-testid={`promo-redemptions-${p.code}`}>{p.redemptions || 0} used</span>
              <label className="flex items-center gap-2 text-sm text-zinc-400">
                <Switch checked={p.is_active} onCheckedChange={(v) => update(p.promo_id, { is_active: v })} data-testid={`promo-toggle-${p.code}`} />
                {p.is_active ? "Active" : "Off"}
              </label>
              <Button variant="ghost" size="icon" onClick={() => remove(p.promo_id)} className="text-zinc-500 hover:text-red-400" data-testid={`promo-delete-${p.code}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
